import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  QrCode, Search, LogOut, User, Building2,
  ArrowDownCircle, ArrowUpCircle, Users, Plus,
  Camera, CheckCircle2, Hash, AlertCircle, FileText, Calendar,
  Package, Pencil, Trash2, Upload, List, Download
} from "lucide-react";
import { formatToTimezone, getTimezone } from "../utils/timezone";
import QRScanner from "../components/QRScanner";
import VisitorPassForm from "../components/VisitorPassForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecurityPortal() {
  const { user, logout, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [scannedPass, setScannedPass] = useState(null);
  const [showPassDialog, setShowPassDialog] = useState(false);
  const [recordingMovement, setRecordingMovement] = useState(false);
  const [showVisitorDialog, setShowVisitorDialog] = useState(false);
  const [visitorPasses, setVisitorPasses] = useState([]);
  const [passCodeInput, setPassCodeInput] = useState("");
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [movements, setMovements] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [purposeFilter, setPurposeFilter] = useState("");
  const [purposeOptions, setPurposeOptions] = useState(["Overnight", "Off Days", "Annual Leave", "Medical", "Other"]);
  const [exitPassReports, setExitPassReports] = useState([]);
  const [reportTab, setReportTab] = useState("movements");
  // Belongings management
  const [belongingsSearch, setBelongingsSearch] = useState("");
  const [belongingsStaff, setBelongingsStaff] = useState(null);
  const [belongingsList, setBelongingsList] = useState([]);
  const [belongingsSearchResults, setBelongingsSearchResults] = useState([]);
  const [searchingBelongingsStaff, setSearchingBelongingsStaff] = useState(false);
  const [showAddBelonging, setShowAddBelonging] = useState(false);
  const [editingBelonging, setEditingBelonging] = useState(null);
  const [belongingName, setBelongingName] = useState("");
  const [belongingDesc, setBelongingDesc] = useState("");
  const [savingBelonging, setSavingBelonging] = useState(false);
  // HR Lists
  const [hrListType, setHrListType] = useState("departure");
  const [hrListData, setHrListData] = useState([]);
  const [loadingHrList, setLoadingHrList] = useState(false);
  const [uploadingHr, setUploadingHr] = useState(false);
  const [appTimezone, setAppTimezone] = useState("Asia/Dhaka");

  useEffect(() => {
    fetchData();
    getTimezone().then(tz => setAppTimezone(tz));
    // Load purpose options from settings
    axios.get(`${API}/settings`, { headers: getAuthHeaders() }).then(res => {
      if (res.data?.purpose_options) {
        setPurposeOptions(typeof res.data.purpose_options === 'string' ? JSON.parse(res.data.purpose_options) : res.data.purpose_options);
      }
    }).catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, visitorsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/visitor-passes?status=active`, { headers: getAuthHeaders() }),
      ]);
      setStats(statsRes.data);
      setVisitorPasses(visitorsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a search term");
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(
        `${API}/security/search?query=${encodeURIComponent(searchQuery)}`,
        { headers: getAuthHeaders() }
      );
      setSearchResults(response.data);
      if (response.data.length === 0) {
        toast.info("No results found");
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleQRScan = async (data) => {
    setShowScanner(false);
    
    try {
      const response = await axios.post(
        `${API}/security/scan`,
        { qr_data: data },
        { headers: getAuthHeaders() }
      );
      
      setScannedPass(response.data);
      setShowPassDialog(true);
    } catch (error) {
      console.error("Scan error:", error);
      toast.error(error.response?.data?.detail || "Invalid or expired QR code");
    }
  };

  const recordMovement = async (passId, type) => {
    setRecordingMovement(true);
    try {
      await axios.post(
        `${API}/security/record-movement`,
        { pass_id: passId, movement_type: type },
        { headers: getAuthHeaders() }
      );
      
      toast.success(`${type === "exit" ? "Exit" : "Arrival"} recorded successfully`);
      setShowPassDialog(false);
      setScannedPass(null);
      fetchData();
    } catch (error) {
      console.error("Record movement error:", error);
      toast.error("Failed to record movement");
    } finally {
      setRecordingMovement(false);
    }
  };

  const handleVisitorCreated = () => {
    setShowVisitorDialog(false);
    fetchData();
    toast.success("Visitor pass created successfully");
  };

  const verifyPassCode = async () => {
    const code = passCodeInput.trim();
    if (!code || code.length !== 6) {
      toast.error("Please enter a valid 6-digit pass code");
      return;
    }

    setVerifyingCode(true);
    try {
      const response = await axios.get(
        `${API}/security/verify-code?code=${encodeURIComponent(code)}`,
        { headers: getAuthHeaders() }
      );
      setScannedPass(response.data);
      setShowPassDialog(true);
      setPassCodeInput("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No approved pass found with this code");
    } finally {
      setVerifyingCode(false);
    }
  };

  const searchBelongingsStaff = async () => {
    if (!belongingsSearch.trim()) return;
    setSearchingBelongingsStaff(true);
    try {
      const res = await axios.get(`${API}/security/search?query=${encodeURIComponent(belongingsSearch)}`, { headers: getAuthHeaders() });
      setBelongingsSearchResults(res.data);
      if (res.data.length === 0) toast.info("No staff found");
    } catch { toast.error("Search failed"); }
    finally { setSearchingBelongingsStaff(false); }
  };

  const selectBelongingsStaff = async (staffUser) => {
    setBelongingsStaff(staffUser);
    setBelongingsSearchResults([]);
    setBelongingsSearch("");
    try {
      const res = await axios.get(`${API}/belongings/staff/${staffUser.id}`, { headers: getAuthHeaders() });
      setBelongingsList(res.data);
    } catch { toast.error("Failed to load belongings"); }
  };

  const saveBelonging = async () => {
    if (!belongingName.trim()) { toast.error("Name is required"); return; }
    setSavingBelonging(true);
    try {
      if (editingBelonging) {
        await axios.put(`${API}/belongings/${editingBelonging.id}`, { name: belongingName, description: belongingDesc }, { headers: getAuthHeaders() });
        toast.success("Belonging updated");
      } else {
        await axios.post(`${API}/belongings`, { staff_id: belongingsStaff.id, name: belongingName, description: belongingDesc }, { headers: getAuthHeaders() });
        toast.success("Belonging added");
      }
      setBelongingName(""); setBelongingDesc(""); setEditingBelonging(null); setShowAddBelonging(false);
      const res = await axios.get(`${API}/belongings/staff/${belongingsStaff.id}`, { headers: getAuthHeaders() });
      setBelongingsList(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSavingBelonging(false); }
  };

  const deleteBelonging = async (itemId) => {
    try {
      await axios.delete(`${API}/belongings/${itemId}`, { headers: getAuthHeaders() });
      toast.success("Belonging removed");
      setBelongingsList(prev => prev.filter(b => b.id !== itemId));
    } catch { toast.error("Failed to delete"); }
  };

  const fetchHrList = async (type) => {
    setLoadingHrList(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await axios.get(`${API}/hr-lists?list_type=${type}&date=${today}`, { headers: getAuthHeaders() });
      setHrListData(res.data);
    } catch {
      // Try without date filter
      try {
        const res = await axios.get(`${API}/hr-lists?list_type=${type}`, { headers: getAuthHeaders() });
        setHrListData(res.data);
      } catch { toast.error("Failed to load HR list"); }
    }
    setLoadingHrList(false);
  };

  const recordHrMovement = async (item, movementType) => {
    try {
      await axios.post(`${API}/security/record-hr-movement`, {
        employee_id: item.employee_id,
        staff_name: item.name,
        movement_type: movementType,
        hr_list_id: item.id,
        department_name: item.department
      }, { headers: getAuthHeaders() });
      toast.success(`${movementType.charAt(0).toUpperCase() + movementType.slice(1)} recorded for ${item.name}`);
      fetchHrList(hrListType);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record"); }
  };

  const fetchMovements = async () => {
    try {
      let url = `${API}/reports/movements`;
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (purposeFilter) params.push(`purpose=${encodeURIComponent(purposeFilter)}`);
      if (params.length) url += `?${params.join("&")}`;
      
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setMovements(res.data.movements || []);
    } catch (error) {
      toast.error("Failed to load reports");
    }
  };

  const fetchExitPassReports = async () => {
    try {
      let url = `${API}/reports/exit-passes`;
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (purposeFilter) params.push(`purpose=${encodeURIComponent(purposeFilter)}`);
      if (params.length) url += `?${params.join("&")}`;
      
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setExitPassReports(res.data || []);
    } catch (error) {
      toast.error("Failed to load exit pass reports");
    }
  };

  const handleFilterReports = () => {
    if (reportTab === "movements") {
      fetchMovements();
    } else {
      fetchExitPassReports();
    }
  };

  const exportToCSV = (type) => {
    let csvContent = "";
    let filename = "";
    
    if (type === "movements") {
      csvContent = "Type,Staff,Employee ID,Department,Recorded By,Date & Time\n";
      movements.forEach(m => {
        csvContent += `${m.movement_type === "exit" ? "Exit" : "Arrival"},"${m.staff_name}",${m.employee_id},"${m.department_name || ""}","${m.security_name}","${formatToTimezone(m.recorded_at, appTimezone)}"\n`;
      });
      filename = `movements_report_${new Date().toISOString().slice(0,10)}.csv`;
    } else {
      csvContent = "Status,Staff,Employee ID,Department,Purpose,Destination,Departure Date,Departure Time,Arrival Date,Arrival Time\n";
      exitPassReports.forEach(p => {
        csvContent += `${p.status},"${p.staff_name}",${p.employee_id},"${p.department_name || ""}","${p.purpose}","${p.destination}",${p.departure_date},${p.departure_time},${p.arrival_date},${p.arrival_time}\n`;
      });
      filename = `exit_passes_report_${new Date().toISOString().slice(0,10)}.csv`;
    }
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("CSV exported successfully");
  };

  const exportToPDF = async (type) => {
    const { default: jsPDF } = await import("jspdf");
    await import("jspdf-autotable");
    
    const doc = new jsPDF({ orientation: "landscape" });
    const title = type === "movements" ? "Staff Movement Report" : "Exit Pass Report";
    const dateStr = new Date().toLocaleDateString();
    
    doc.setFontSize(18);
    doc.text(title, 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${dateStr}${dateFrom ? ` | From: ${dateFrom}` : ""}${dateTo ? ` | To: ${dateTo}` : ""}${purposeFilter && purposeFilter !== "all" ? ` | Purpose: ${purposeFilter}` : ""}`, 14, 28);
    
    if (type === "movements") {
      const headers = [["Type", "Staff", "Employee ID", "Department", "Recorded By", "Date & Time"]];
      const rows = movements.map(m => [
        m.movement_type === "exit" ? "Exit" : "Arrival",
        m.staff_name,
        m.employee_id,
        m.department_name || "—",
        m.security_name,
        formatToTimezone(m.recorded_at, appTimezone)
      ]);
      doc.autoTable({ head: headers, body: rows, startY: 34, styles: { fontSize: 9 }, headStyles: { fillColor: [30, 70, 50] } });
      doc.setFontSize(9);
      doc.text(`Total: ${movements.length} record(s)`, 14, doc.lastAutoTable.finalY + 10);
    } else {
      const headers = [["Status", "Staff", "Employee ID", "Department", "Purpose", "Destination", "Departure", "Arrival"]];
      const rows = exitPassReports.map(p => [
        p.status,
        p.staff_name,
        p.employee_id,
        p.department_name || "—",
        p.purpose,
        p.destination,
        `${p.departure_date} ${p.departure_time}`,
        `${p.arrival_date} ${p.arrival_time}`
      ]);
      doc.autoTable({ head: headers, body: rows, startY: 34, styles: { fontSize: 9 }, headStyles: { fillColor: [30, 70, 50] } });
      doc.setFontSize(9);
      doc.text(`Total: ${exitPassReports.length} record(s)`, 14, doc.lastAutoTable.finalY + 10);
    }
    
    const filename = type === "movements" 
      ? `movements_report_${new Date().toISOString().slice(0,10)}.pdf`
      : `exit_passes_report_${new Date().toISOString().slice(0,10)}.pdf`;
    doc.save(filename);
    toast.success("PDF exported successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-pulse text-primary font-heading text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-800 to-emerald-600 text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <QrCode className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-heading font-bold">Security Portal</h1>
                <p className="text-xs text-emerald-100">Exit Pass Scanner</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-emerald-200">{user?.employee_id}</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={logout}
                data-testid="logout-btn"
                className="text-white hover:bg-white/20"
              >
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border-stone-200 shadow-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 font-medium uppercase tracking-wider">Today's Exits</p>
                  <p className="text-3xl font-heading font-bold text-stone-800">{stats.today_exits || 0}</p>
                </div>
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                  <ArrowUpCircle className="w-6 h-6 text-red-500" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-stone-200 shadow-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 font-medium uppercase tracking-wider">Today's Arrivals</p>
                  <p className="text-3xl font-heading font-bold text-stone-800">{stats.today_arrivals || 0}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <ArrowDownCircle className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-stone-200 shadow-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 font-medium uppercase tracking-wider">Active Visitors</p>
                  <p className="text-3xl font-heading font-bold text-stone-800">{stats.active_visitors || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-primary to-emerald-700 text-white shadow-float cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => setShowScanner(true)}
            data-testid="open-scanner-btn"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-emerald-100 font-medium uppercase tracking-wider">Scan QR</p>
                  <p className="text-lg font-heading font-bold mt-1">Open Scanner</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Camera className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="scanner" className="space-y-4">
          <TabsList className="!flex bg-white border border-stone-200 p-1 rounded-xl w-full overflow-x-auto gap-0.5 no-scrollbar h-auto">
            <TabsTrigger value="scanner" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs flex-1 min-w-0 px-1.5 sm:px-3 py-2">
              <QrCode className="w-4 h-4 sm:mr-1.5 flex-shrink-0" />
              <span className="hidden sm:inline">Scan</span>
            </TabsTrigger>
            <TabsTrigger value="passcode" data-testid="passcode-tab" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs flex-1 min-w-0 px-1.5 sm:px-3 py-2">
              <Hash className="w-4 h-4 sm:mr-1.5 flex-shrink-0" />
              <span className="hidden sm:inline">Code</span>
            </TabsTrigger>
            <TabsTrigger value="search" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs flex-1 min-w-0 px-1.5 sm:px-3 py-2">
              <Search className="w-4 h-4 sm:mr-1.5 flex-shrink-0" />
              <span className="hidden sm:inline">Search</span>
            </TabsTrigger>
            <TabsTrigger value="belongings" data-testid="belongings-tab" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs flex-1 min-w-0 px-1.5 sm:px-3 py-2">
              <Package className="w-4 h-4 sm:mr-1.5 flex-shrink-0" />
              <span className="hidden sm:inline">Items</span>
            </TabsTrigger>
            <TabsTrigger value="hrlists" data-testid="hrlists-tab" onClick={() => fetchHrList(hrListType)} className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs flex-1 min-w-0 px-1.5 sm:px-3 py-2">
              <List className="w-4 h-4 sm:mr-1.5 flex-shrink-0" />
              <span className="hidden sm:inline">HR</span>
            </TabsTrigger>
            <TabsTrigger value="visitors" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs flex-1 min-w-0 px-1.5 sm:px-3 py-2">
              <Users className="w-4 h-4 sm:mr-1.5 flex-shrink-0" />
              <span className="hidden sm:inline">Visitors</span>
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="reports-tab" onClick={() => { if (reportTab === "movements") fetchMovements(); else fetchExitPassReports(); }} className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs flex-1 min-w-0 px-1.5 sm:px-3 py-2">
              <FileText className="w-4 h-4 sm:mr-1.5 flex-shrink-0" />
              <span className="hidden sm:inline">Reports</span>
            </TabsTrigger>
          </TabsList>

          {/* Scanner Tab */}
          <TabsContent value="scanner">
            <Card className="bg-white border-stone-200">
              <CardContent className="p-8 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-24 h-24 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <QrCode className="w-12 h-12 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-stone-800 mb-2">Scan Exit Pass</h3>
                  <p className="text-stone-500 mb-6">
                    Use your device camera to scan staff exit pass QR codes
                  </p>
                  <Button
                    onClick={() => setShowScanner(true)}
                    data-testid="scan-qr-btn"
                    className="bg-primary hover:bg-primary/90 text-white gap-2 rounded-xl px-8 py-6 text-lg shadow-lg shadow-primary/20"
                  >
                    <Camera className="w-5 h-5" strokeWidth={2} />
                    Open Camera Scanner
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pass Code Tab */}
          <TabsContent value="passcode">
            <Card className="bg-white border-stone-200">
              <CardContent className="p-8 text-center">
                <div className="max-w-sm mx-auto">
                  <div className="w-24 h-24 bg-amber-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <Hash className="w-12 h-12 text-amber-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-stone-800 mb-2">Enter Pass Code</h3>
                  <p className="text-stone-500 mb-6">
                    Ask the staff member for their 6-digit pass code
                  </p>
                  <div className="space-y-4">
                    <Input
                      data-testid="pass-code-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      value={passCodeInput}
                      onChange={(e) => setPassCodeInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      onKeyDown={(e) => e.key === "Enter" && verifyPassCode()}
                      className="h-16 text-center text-3xl font-mono tracking-[0.4em] font-bold border-2 border-stone-300 focus:border-primary"
                    />
                    <Button
                      onClick={verifyPassCode}
                      disabled={verifyingCode || passCodeInput.length !== 6}
                      data-testid="verify-code-btn"
                      className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-14 text-lg shadow-lg shadow-primary/20"
                    >
                      {verifyingCode ? "Verifying..." : "Verify Code"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Search Tab */}
          <TabsContent value="search">
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <CardTitle className="font-heading">Manual Search</CardTitle>
                <CardDescription>Search for staff by name or employee ID</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-6">
                  <div className="flex-1">
                    <Input
                      data-testid="search-input"
                      placeholder="Enter employee ID or name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      className="h-12"
                    />
                  </div>
                  <Button
                    onClick={handleSearch}
                    disabled={searching}
                    data-testid="search-btn"
                    className="bg-primary hover:bg-primary/90 rounded-xl px-6"
                  >
                    {searching ? "Searching..." : "Search"}
                  </Button>
                </div>

                {searchResults.length > 0 && (
                  <div className="space-y-4">
                    {searchResults.map((result, idx) => (
                      <Card key={idx} className="border-stone-200">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
                              </div>
                              <div>
                                <h4 className="font-semibold text-stone-800">{result.user.name}</h4>
                                <p className="text-sm text-stone-500 font-mono">{result.user.employee_id}</p>
                              </div>
                            </div>
                          </div>
                          
                          {result.active_passes.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-stone-100">
                              <p className="text-xs text-stone-400 uppercase tracking-wider mb-2">Active Exit Passes</p>
                              <div className="space-y-2">
                                {result.active_passes.map((pass) => {
                                  const exitDone = pass.exit_recorded;
                                  const arrivalDone = pass.arrival_recorded;
                                  const bothDone = exitDone && arrivalDone;
                                  return (
                                    <div key={pass.id} className="bg-stone-50 rounded-lg p-3">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="text-sm font-medium text-stone-700">{pass.destination}</p>
                                          <p className="text-xs text-stone-500">{pass.departure_date} - {pass.arrival_date}</p>
                                          {pass.exit_recorded_at && <p className="text-xs text-red-500 mt-0.5">Exit: {formatToTimezone(pass.exit_recorded_at, appTimezone)}</p>}
                                          {pass.arrival_recorded_at && <p className="text-xs text-emerald-500">Arrival: {formatToTimezone(pass.arrival_recorded_at, appTimezone)}</p>}
                                        </div>
                                        <div className="flex gap-2">
                                          {!bothDone && (
                                            <>
                                              <Button size="sm" onClick={() => recordMovement(pass.id, "exit")} disabled={exitDone}
                                                data-testid={`exit-btn-${pass.id}`}
                                                className={`rounded-lg ${exitDone ? "bg-stone-300 cursor-not-allowed" : "bg-red-500 hover:bg-red-600"} text-white`}>
                                                <ArrowUpCircle className="w-4 h-4 mr-1" /> {exitDone ? "Exited" : "Exit"}
                                              </Button>
                                              <Button size="sm" onClick={() => recordMovement(pass.id, "arrival")} disabled={!exitDone || arrivalDone}
                                                data-testid={`arrival-btn-${pass.id}`}
                                                className={`rounded-lg ${(!exitDone || arrivalDone) ? "bg-stone-300 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}>
                                                <ArrowDownCircle className="w-4 h-4 mr-1" /> {arrivalDone ? "Arrived" : "Arrival"}
                                              </Button>
                                            </>
                                          )}
                                          {bothDone && <span className="text-xs text-stone-400 font-medium bg-stone-200 px-3 py-1.5 rounded-lg">Completed</span>}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Belongings in search */}
                          {result.belongings && result.belongings.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-stone-100">
                              <p className="text-xs text-amber-700 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
                                <Package className="w-3.5 h-3.5" /> Belongings ({result.belongings.length})
                              </p>
                              <div className="space-y-1">
                                {result.belongings.map((b) => (
                                  <div key={b.id} className="flex items-center gap-2 text-sm">
                                    <span className="text-amber-500">-</span>
                                    <span className="font-medium text-stone-700">{b.name}</span>
                                    {b.description && <span className="text-stone-500">({b.description})</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Belongings Tab */}
          <TabsContent value="belongings">
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <CardTitle className="font-heading">Staff Belongings</CardTitle>
                <CardDescription>Search for a staff member to manage their registered belongings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!belongingsStaff ? (
                  <>
                    <div className="flex gap-3">
                      <Input
                        data-testid="belongings-search-input"
                        placeholder="Search staff by name or employee ID..."
                        value={belongingsSearch}
                        onChange={(e) => setBelongingsSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && searchBelongingsStaff()}
                        className="h-12"
                      />
                      <Button onClick={searchBelongingsStaff} disabled={searchingBelongingsStaff} data-testid="belongings-search-btn" className="bg-primary hover:bg-primary/90 rounded-xl px-6">
                        {searchingBelongingsStaff ? "..." : "Search"}
                      </Button>
                    </div>
                    {belongingsSearchResults.length > 0 && (
                      <div className="space-y-2">
                        {belongingsSearchResults.map((r, i) => (
                          <div key={i} onClick={() => selectBelongingsStaff(r.user)} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors" data-testid={`belongings-staff-${r.user.employee_id}`}>
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
                            </div>
                            <div>
                              <p className="font-semibold text-stone-800">{r.user.name}</p>
                              <p className="text-sm text-stone-500 font-mono">{r.user.employee_id}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="font-semibold text-stone-800">{belongingsStaff.name}</p>
                          <p className="text-sm text-stone-500 font-mono">{belongingsStaff.employee_id}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setBelongingsStaff(null); setBelongingsList([]); }} className="rounded-lg">
                          Change Staff
                        </Button>
                        <Button size="sm" onClick={() => { setShowAddBelonging(true); setEditingBelonging(null); setBelongingName(""); setBelongingDesc(""); }} data-testid="add-belonging-btn" className="bg-primary hover:bg-primary/90 text-white rounded-lg gap-1.5">
                          <Plus className="w-4 h-4" /> Add Item
                        </Button>
                      </div>
                    </div>

                    {showAddBelonging && (
                      <Card className="border-primary/20 bg-primary/5">
                        <CardContent className="p-4 space-y-3">
                          <p className="font-semibold text-sm text-stone-700">{editingBelonging ? "Edit Belonging" : "Add New Belonging"}</p>
                          <Input data-testid="belonging-name-input" placeholder="Item name (e.g., Laptop)" value={belongingName} onChange={(e) => setBelongingName(e.target.value)} className="h-10" />
                          <Input data-testid="belonging-desc-input" placeholder="Description / Serial number (optional)" value={belongingDesc} onChange={(e) => setBelongingDesc(e.target.value)} className="h-10" />
                          <div className="flex gap-2">
                            <Button onClick={saveBelonging} disabled={savingBelonging} data-testid="save-belonging-btn" className="bg-primary text-white rounded-lg" size="sm">
                              {savingBelonging ? "Saving..." : editingBelonging ? "Update" : "Add"}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => { setShowAddBelonging(false); setEditingBelonging(null); }} className="rounded-lg">Cancel</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {belongingsList.length === 0 ? (
                      <div className="text-center py-12">
                        <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                        <p className="text-stone-500">No belongings registered for this staff member</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {belongingsList.map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl" data-testid={`belonging-item-${item.id}`}>
                            <div className="flex items-center gap-3">
                              <Package className="w-5 h-5 text-primary" strokeWidth={1.5} />
                              <div>
                                <p className="font-medium text-stone-800">{item.name}</p>
                                {item.description && <p className="text-sm text-stone-500">{item.description}</p>}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setEditingBelonging(item); setBelongingName(item.name); setBelongingDesc(item.description || ""); setShowAddBelonging(true); }} data-testid={`edit-belonging-${item.id}`} className="text-stone-400 hover:text-primary">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteBelonging(item.id)} data-testid={`delete-belonging-${item.id}`} className="text-stone-400 hover:text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* HR Lists Tab */}
          <TabsContent value="hrlists">
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <CardTitle className="font-heading">HR Departure / Arrival Lists</CardTitle>
                <CardDescription>View daily departure and arrival lists uploaded by HR</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Button variant={hrListType === "departure" ? "default" : "outline"} onClick={() => { setHrListType("departure"); fetchHrList("departure"); }}
                    data-testid="hr-departure-tab" className={`rounded-lg flex-1 ${hrListType === "departure" ? "bg-red-500 hover:bg-red-600 text-white" : ""}`}>
                    <ArrowUpCircle className="w-4 h-4 mr-1.5" /> Departures
                  </Button>
                  <Button variant={hrListType === "arrival" ? "default" : "outline"} onClick={() => { setHrListType("arrival"); fetchHrList("arrival"); }}
                    data-testid="hr-arrival-tab" className={`rounded-lg flex-1 ${hrListType === "arrival" ? "bg-emerald-500 hover:bg-emerald-600 text-white" : ""}`}>
                    <ArrowDownCircle className="w-4 h-4 mr-1.5" /> Arrivals
                  </Button>
                </div>

                {loadingHrList ? (
                  <div className="text-center py-8 text-stone-400">Loading...</div>
                ) : hrListData.length === 0 ? (
                  <div className="text-center py-12">
                    <List className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500">No {hrListType} records found for today</p>
                    <p className="text-xs text-stone-400 mt-1">Ask HR/Admin to upload the daily list</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {hrListData.map((item) => (
                      <Card key={item.id} className={`border-stone-200 ${item.system_user_id ? '' : 'border-l-4 border-l-amber-300'}`}>
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.system_user_id ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                                <User className={`w-4 h-4 ${item.system_user_id ? 'text-emerald-600' : 'text-amber-600'}`} strokeWidth={1.5} />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-stone-800 text-sm truncate">{item.name}</p>
                                  {item.system_user_id ? (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium whitespace-nowrap">Linked</span>
                                  ) : (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium whitespace-nowrap">Not in system</span>
                                  )}
                                </div>
                                <p className="text-xs text-stone-500 font-mono">{item.employee_id}</p>
                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-stone-400">
                                  {item.department && <span>{item.department}</span>}
                                  {item.position && <span>- {item.position}</span>}
                                  {item.purpose && <span>- {item.purpose}</span>}
                                  {item.destination && <span>- {item.destination}</span>}
                                  {item.travel_req && <span>- {item.travel_req}</span>}
                                  {item.flight && <span>- {item.flight}</span>}
                                  {item.date && <span>- {item.date}</span>}
                                  {item.time && <span>- {item.time}</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                              {item.status && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.status === "Approved" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{item.status}</span>}
                              {item.movement_recorded ? (
                                <span className="text-xs text-stone-400 bg-stone-100 px-2 py-1 rounded-lg">Recorded</span>
                              ) : (
                                <Button size="sm" onClick={() => recordHrMovement(item, hrListType === "departure" ? "exit" : "arrival")}
                                  data-testid={`hr-record-${item.id}`}
                                  className={`rounded-lg text-xs ${hrListType === "departure" ? "bg-red-500 hover:bg-red-600" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}>
                                  {hrListType === "departure" ? <><ArrowUpCircle className="w-3.5 h-3.5 mr-1" /> Record Exit</> : <><ArrowDownCircle className="w-3.5 h-3.5 mr-1" /> Record Arrival</>}
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Belongings */}
                          {item.belongings && item.belongings.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-stone-100">
                              <p className="text-xs text-amber-700 font-semibold flex items-center gap-1 mb-1">
                                <Package className="w-3 h-3" /> Belongings
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {item.belongings.map((b) => (
                                  <span key={b.id} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded">{b.name}{b.description ? ` (${b.description})` : ""}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Visitors Tab */}
          <TabsContent value="visitors">
            <Card className="bg-white border-stone-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Visitor Passes</CardTitle>
                  <CardDescription>Create and manage temporary visitor passes</CardDescription>
                </div>
                <Button
                  onClick={() => setShowVisitorDialog(true)}
                  data-testid="new-visitor-btn"
                  className="bg-primary hover:bg-primary/90 rounded-xl gap-2"
                >
                  <Plus className="w-4 h-4" />
                  New Visitor Pass
                </Button>
              </CardHeader>
              <CardContent>
                {visitorPasses.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-stone-400" strokeWidth={1.5} />
                    </div>
                    <p className="text-stone-500">No active visitor passes</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {visitorPasses.map((visitor) => (
                      <Card key={visitor.id} className="border-stone-200">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-semibold text-stone-800">{visitor.visitor_name}</h4>
                              <p className="text-sm text-stone-500">Host: {visitor.host_name}</p>
                            </div>
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                          </div>
                          <div className="text-sm text-stone-500 space-y-1">
                            <p>Valid: {visitor.valid_from} - {visitor.valid_until}</p>
                            <p>Purpose: {visitor.purpose}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <CardTitle className="font-heading">Reports</CardTitle>
                <CardDescription>View staff movements and exit pass records</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filter Controls */}
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">From</label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-40"
                      data-testid="report-date-from"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">To</label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-40"
                      data-testid="report-date-to"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">Purpose</label>
                    <Select value={purposeFilter} onValueChange={setPurposeFilter}>
                      <SelectTrigger className="w-44" data-testid="report-purpose-filter">
                        <SelectValue placeholder="All Purposes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Purposes</SelectItem>
                        {purposeOptions.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleFilterReports} data-testid="report-filter-btn" className="bg-primary text-white rounded-xl">
                    Filter
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => exportToCSV(reportTab === "movements" ? "movements" : "exit-passes")}
                    disabled={(reportTab === "movements" ? movements.length : exitPassReports.length) === 0}
                    data-testid="report-export-csv"
                    className="rounded-xl"
                  >
                    <Download className="w-4 h-4 mr-1.5" /> CSV
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => exportToPDF(reportTab === "movements" ? "movements" : "exit-passes")}
                    disabled={(reportTab === "movements" ? movements.length : exitPassReports.length) === 0}
                    data-testid="report-export-pdf"
                    className="rounded-xl"
                  >
                    <Download className="w-4 h-4 mr-1.5" /> PDF
                  </Button>
                </div>

                {/* Report Type Tabs */}
                <div className="flex gap-2 border-b border-stone-200 pb-1">
                  <button
                    onClick={() => { setReportTab("movements"); fetchMovements(); }}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${reportTab === "movements" ? "bg-primary text-white" : "text-stone-500 hover:text-stone-800 hover:bg-stone-100"}`}
                    data-testid="report-movements-tab"
                  >
                    Movements
                  </button>
                  <button
                    onClick={() => { setReportTab("exit-passes"); fetchExitPassReports(); }}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${reportTab === "exit-passes" ? "bg-primary text-white" : "text-stone-500 hover:text-stone-800 hover:bg-stone-100"}`}
                    data-testid="report-exitpasses-tab"
                  >
                    Exit Passes
                  </button>
                </div>

                {/* Movements Report */}
                {reportTab === "movements" && (
                  <>
                    {movements.length === 0 ? (
                      <div className="text-center py-12 text-stone-400">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p className="font-medium">No movement records found</p>
                        <p className="text-sm">Try adjusting the date range or purpose filter</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <p className="text-sm text-stone-500 mb-2">{movements.length} record(s) found</p>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-stone-200 text-left">
                              <th className="py-3 px-3 font-semibold text-stone-600">Type</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Staff</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Employee ID</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Department</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Recorded By</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Date & Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {movements.map((m) => (
                              <tr key={m.id} className="border-b border-stone-100 hover:bg-stone-50">
                                <td className="py-3 px-3">
                                  <Badge className={m.movement_type === "exit"
                                    ? "bg-red-50 text-red-700 border-red-200"
                                    : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  }>
                                    {m.movement_type === "exit" ? "Exit" : "Arrival"}
                                  </Badge>
                                </td>
                                <td className="py-3 px-3 font-medium text-stone-800">{m.staff_name}</td>
                                <td className="py-3 px-3 font-mono text-stone-600">{m.employee_id}</td>
                                <td className="py-3 px-3 text-stone-600">{m.department_name || "—"}</td>
                                <td className="py-3 px-3 text-stone-600">{m.security_name}</td>
                                <td className="py-3 px-3 text-stone-500">
                                  {formatToTimezone(m.recorded_at, appTimezone)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {/* Exit Passes Report */}
                {reportTab === "exit-passes" && (
                  <>
                    {exitPassReports.length === 0 ? (
                      <div className="text-center py-12 text-stone-400">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p className="font-medium">No exit pass records found</p>
                        <p className="text-sm">Try adjusting the date range or purpose filter</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <p className="text-sm text-stone-500 mb-2">{exitPassReports.length} record(s) found</p>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-stone-200 text-left">
                              <th className="py-3 px-3 font-semibold text-stone-600">Status</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Staff</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Employee ID</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Department</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Purpose</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Destination</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Departure</th>
                              <th className="py-3 px-3 font-semibold text-stone-600">Arrival</th>
                            </tr>
                          </thead>
                          <tbody>
                            {exitPassReports.map((p) => (
                              <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50">
                                <td className="py-3 px-3">
                                  <Badge className={
                                    p.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                    p.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                    p.status === "used" ? "bg-blue-50 text-blue-700 border-blue-200" :
                                    "bg-red-50 text-red-700 border-red-200"
                                  }>
                                    {p.status}
                                  </Badge>
                                </td>
                                <td className="py-3 px-3 font-medium text-stone-800">{p.staff_name}</td>
                                <td className="py-3 px-3 font-mono text-stone-600">{p.employee_id}</td>
                                <td className="py-3 px-3 text-stone-600">{p.department_name || "—"}</td>
                                <td className="py-3 px-3">
                                  <Badge variant="outline" className="text-stone-600">{p.purpose}</Badge>
                                </td>
                                <td className="py-3 px-3 text-stone-600">{p.destination}</td>
                                <td className="py-3 px-3 text-stone-500">{p.departure_date} {p.departure_time}</td>
                                <td className="py-3 px-3 text-stone-500">{p.arrival_date} {p.arrival_time}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner 
          onScan={handleQRScan} 
          onClose={() => setShowScanner(false)} 
        />
      )}

      {/* Scanned Pass Dialog */}
      <Dialog open={showPassDialog} onOpenChange={setShowPassDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {scannedPass?.data?.is_used ? (
                <>
                  <AlertCircle className="w-5 h-5 text-stone-400" />
                  Expired Exit Pass
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Valid Exit Pass
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {scannedPass && (
            <div className="space-y-4">
              <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                      <User className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-stone-800">{scannedPass.data?.staff_name}</h3>
                      <p className="text-sm text-stone-600 font-mono">{scannedPass.data?.employee_id}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Building2 className="w-3 h-3 text-stone-400" />
                        <span className="text-xs text-stone-500">{scannedPass.data?.department || scannedPass.data?.department_name || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Destination</p>
                  <p className="font-medium text-stone-700">{scannedPass.data?.destination || "N/A"}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Transport</p>
                  <p className="font-medium text-stone-700">{scannedPass.data?.transport_type || "N/A"}</p>
                  {scannedPass.data?.transport_details && (
                    <p className="text-xs text-stone-500">{scannedPass.data.transport_details}</p>
                  )}
                </div>
              </div>

              {scannedPass.data?.purpose && (
                <div className="bg-stone-50 rounded-xl p-3 text-sm">
                  <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Purpose</p>
                  <p className="font-medium text-stone-700">{scannedPass.data.purpose}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Departure</p>
                  <p className="font-medium text-stone-700">{scannedPass.data?.departure_date}</p>
                  <p className="text-stone-500">{scannedPass.data?.departure_time}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Return</p>
                  <p className="font-medium text-stone-700">{scannedPass.data?.arrival_date}</p>
                  <p className="text-stone-500">{scannedPass.data?.arrival_time}</p>
                </div>
              </div>

              {/* Belongings Section */}
              {scannedPass.data?.belongings && scannedPass.data.belongings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="scan-belongings-section">
                  <p className="text-xs text-amber-700 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
                    <Package className="w-3.5 h-3.5" /> Staff Belongings ({scannedPass.data.belongings.length})
                  </p>
                  <div className="space-y-1.5">
                    {scannedPass.data.belongings.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-amber-500 mt-0.5">-</span>
                        <div>
                          <span className="font-medium text-stone-800">{item.name}</span>
                          {item.description && <span className="text-stone-500 ml-1">({item.description})</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {scannedPass.data?.is_used && scannedPass.data?.exit_recorded && scannedPass.data?.arrival_recorded ? (
                <div className="bg-stone-100 border border-stone-300 rounded-xl p-4 text-center">
                  <p className="font-semibold text-stone-600">Pass Expired</p>
                  <p className="text-sm text-stone-500 mt-1">Both exit and arrival have been recorded</p>
                </div>
              ) : (
                <div className="space-y-3 pt-4">
                  {scannedPass.data?.exit_recorded && (
                    <p className="text-xs text-center text-emerald-600 font-medium">Exit already recorded</p>
                  )}
                  {scannedPass.data?.arrival_recorded && (
                    <p className="text-xs text-center text-emerald-600 font-medium">Arrival already recorded</p>
                  )}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => recordMovement(scannedPass.data?.pass_id || scannedPass.data?.id, "exit")}
                      disabled={recordingMovement || scannedPass.data?.exit_recorded}
                      data-testid="record-exit-btn"
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white gap-2 rounded-xl h-14 disabled:opacity-40"
                    >
                      <ArrowUpCircle className="w-5 h-5" strokeWidth={2} />
                      {scannedPass.data?.exit_recorded ? "Exit Recorded" : "Record Exit"}
                    </Button>
                    <Button
                      onClick={() => recordMovement(scannedPass.data?.pass_id || scannedPass.data?.id, "arrival")}
                      disabled={recordingMovement || scannedPass.data?.arrival_recorded}
                      data-testid="record-arrival-btn"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2 rounded-xl h-14 disabled:opacity-40"
                    >
                      <ArrowDownCircle className="w-5 h-5" strokeWidth={2} />
                      {scannedPass.data?.arrival_recorded ? "Arrival Recorded" : "Record Arrival"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Visitor Pass Dialog */}
      <Dialog open={showVisitorDialog} onOpenChange={setShowVisitorDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Visitor Pass</DialogTitle>
            <DialogDescription>Generate a temporary QR code pass for visitors</DialogDescription>
          </DialogHeader>
          <VisitorPassForm onSuccess={handleVisitorCreated} onCancel={() => setShowVisitorDialog(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
